import React from 'react'
import classNames from 'classnames'
import Link from 'next/link'
import classes from '@src/styles/vs-competition.module.css'
import { segmentTrack } from '@src/functions/segment'
import useAppLink from '@src/hooks/useAppLink'

/* -------------------------------------------------------------------------- */
/* Three things change on day one                                             */
/* -------------------------------------------------------------------------- */

export type ReasonsProps = {
  heading: string
  list: { heading: string; content: string }[]
}

/**
 * Numbered because the three are a ranked answer to "what do I actually get",
 * not an unordered feature list — 01 is the one that changes the buyer's day.
 */
export function Reasons({ heading, list }: ReasonsProps) {
  return (
    <section className={classNames(classes.section, classes.sectionTint)}>
      <div className={classes.inner}>
        <div className={classes.headWrap}>
          <h2 className={classes.heading}>{heading}</h2>
        </div>
        <div className={classes.reasons}>
          {list.map((reason, index) => (
            <div key={reason.heading} className={classes.reason}>
              <b className={classNames(classes.reasonNumber, classes.tnum)}>
                {String(index + 1).padStart(2, '0')}
              </b>
              <h3 className={classes.reasonHeading}>{reason.heading}</h3>
              <p className={classes.reasonBody}>{reason.content}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* What we take                                                               */
/* -------------------------------------------------------------------------- */

export type StreamsProps = {
  heading: string
  chips: string[]
  note: string
}

export function Streams({ heading, chips, note }: StreamsProps) {
  return (
    <section className={classes.section}>
      <div className={classes.inner}>
        <div className={classes.headWrap}>
          <h2 className={classes.heading}>{heading}</h2>
        </div>
        <div className={classes.chips}>
          {chips.map((chip) => (
            <span key={chip} className={classes.chip}>
              {chip}
            </span>
          ))}
        </div>
        <p className={classes.note}>{note}</p>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Switching, answered                                                        */
/* -------------------------------------------------------------------------- */

export type FaqProps = {
  heading: string
  questions: { question: string; answer: string }[]
}

/**
 * Native <details> so the answers are in the DOM unconditionally — the FAQPage
 * schema on this page promises them, and a JS accordion that mounts empty would
 * make the markup a lie.
 */
export function Faq({ heading, questions }: FaqProps) {
  return (
    <section className={classNames(classes.section, classes.sectionTint)}>
      <div className={classes.inner}>
        <div className={classes.headWrap}>
          <h2 className={classes.heading}>{heading}</h2>
        </div>
        <div className={classes.faq}>
          {questions.map((item, index) => (
            <details key={item.question} className={classes.faqItem} open={index === 0}>
              <summary className={classes.faqSummary}>{item.question}</summary>
              <p className={classes.faqAnswer}>{item.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Closing CTA                                                                */
/* -------------------------------------------------------------------------- */

export type ClosingCtaProps = {
  heading: string
  ctaText: string
  ctaLink: string
  /** See Hero's onQuoteClick — the link stays real, the handler intercepts it. */
  onQuoteClick?: () => void
}

export function ClosingCta({ heading, ctaText, ctaLink, onQuoteClick }: ClosingCtaProps) {
  const appLink = useAppLink()

  return (
    <section className={classNames(classes.section, classes.sectionDark)}>
      <div className={classNames(classes.inner, classes.closing)}>
        <h2>{heading}</h2>
        <div className={classes.closingCta}>
          <Link
            className={classes.quoteButton}
            href={appLink(ctaLink)}
            onClick={(event) => {
              segmentTrack('Click', { button: ctaText, page: 'Vs Competition' })
              if (!onQuoteClick) return
              event.preventDefault()
              onQuoteClick()
            }}
          >
            {ctaText}
          </Link>
        </div>
      </div>
    </section>
  )
}
